import { useState, useRef, useCallback, useEffect } from 'react';

interface UseCameraProps {
  onError: (message: string) => void;
}

interface UseCameraReturn {
  videoRef: React.RefObject<HTMLVideoElement>;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  stream: MediaStream | null;
  cameraActive: boolean;
  isLoading: boolean;
  startCamera: () => Promise<void>;
  stopCamera: () => void;
  captureImage: () => string | null;
  setCameraActive: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useCamera = ({ onError }: UseCameraProps): UseCameraReturn => {
  const videoRef = useRef<HTMLVideoElement>(null) as React.RefObject<HTMLVideoElement>;
  const canvasRef = useRef<HTMLCanvasElement>(null) as React.RefObject<HTMLCanvasElement>;
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Stop the camera
  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((track) => {
        track.stop();
      });
      setStream(null);
    }
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
  }, [stream]);

  // Initialize camera
  const startCamera = useCallback(async () => {
    setIsLoading(true);

    try {
      // Check if navigator.mediaDevices is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error(
          "Camera access is not supported in your browser. Please try a different browser."
        );
      }

      // First set camera active to ensure the video element is rendered
      setCameraActive(true);

      // Wait for the video element to be available in the DOM
      await new Promise((resolve) => setTimeout(resolve, 1000));

      if (!videoRef.current) {
        throw new Error("Video element not initialized properly");
      }

      // Stop any existing stream
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }

      // Request camera access
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment", // Use back camera on mobile devices
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });

      // Set the stream to the video element
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;

        // Apply direct styles to ensure visibility
        videoRef.current.style.width = "100%";
        videoRef.current.style.height = "100%";
        videoRef.current.style.objectFit = "cover";
        videoRef.current.style.display = "block";
        videoRef.current.style.backgroundColor = "#000";

        // Ensure the video plays
        try {
          await videoRef.current.play();
        } catch (playError) {
          console.error("Error playing video:", playError);
        }
      } else {
        console.error("Video element not available after waiting");
      }

      // Store the stream
      setStream(mediaStream);
      setIsLoading(false);
    } catch (err) {
      console.error("Error starting camera:", err);
      onError(
        `Failed to access camera: ${
          err instanceof Error ? err.message : String(err)
        }`
      );
      setCameraActive(false);
      setIsLoading(false);
    }
  }, [stream, onError]);

  // Capture an image from the video stream
  const captureImage = useCallback((): string | null => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");

      if (context) {
        // Set canvas dimensions to match video
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // Draw the current video frame to the canvas
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Convert canvas to data URL with high quality
        const imageDataUrl = canvas.toDataURL("image/jpeg", 0.95);

        // Stop the camera after capturing
        stopCamera();
        
        return imageDataUrl;
      }
    }
    return null;
  }, [stopCamera]);

  // Effect to clean up camera on component unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
        setStream(null);
      }
    };
  }, [stream]);

  // Effect to ensure video element is properly initialized after stream is set
  useEffect(() => {
    if (stream && videoRef.current) {
      // This should be redundant now, but keeping it as a safeguard
      if (videoRef.current.srcObject !== stream) {
        videoRef.current.srcObject = stream;
      }
    }
  }, [stream]);

  return {
    videoRef,
    canvasRef,
    stream,
    cameraActive,
    isLoading,
    startCamera,
    stopCamera,
    captureImage,
    setCameraActive,
  };
};
